import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { FactType, Threat } from '@/api/rest/resources/conservation';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { CapsuleTrend } from '@/components';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useProject } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProject';
import { getAreaPct } from '@/pages/shared/projects/pages/project/pages/conservation/shared/utils/getAreaPct';
import { getChangePct } from '@/pages/shared/projects/pages/project/pages/conservation/shared/utils/getChangePct';
import { Area } from '@/utils/area';
import { isNullish } from '@/utils/booleans';

const NO_DATA = '-';
const UNIT_LABEL = 'global.ui.unit.%';

export const TableTile = () => {
  const { t } = useTranslation();

  const years = useHabitatIntactnessYears();

  return (
    <div className='flex flex-col gap-8'>
      <ChartHeading
        heading={t('shared.ncaDetail.habitatIntactness.degradationDriversTable.chartHeading', { year: years.min })}
        className='w-full px-6 sm:px-0'
      />
      <DegradationDrivers />
    </div>
  );
};

const DegradationDrivers = () => {
  const { t } = useTranslation();

  const { min: baselineYear, max: currentYear } = useHabitatIntactnessYears();
  const project = useProject();
  const threatsData = useThreatData();

  if (typeof baselineYear !== 'number' || typeof currentYear !== 'number') {
    return null;
  }

  const years = Array.from({ length: currentYear - baselineYear + 1 }, (_, i) => (baselineYear + i).toString());
  const yearsTimeSpan = currentYear - baselineYear + 1;

  const dimension = project?.areaUnits === 'ha' ? 'hectares' : 'squareKilometers';

  const projectAreaInHectares = project?.area
    ? new Area({
        value: project.area,
        dimension,
      }).toHectares().value
    : null;

  return (
    <Stack>
      <div className='w-full overflow-x-auto'>
        <table
          className='typography-body2 w-full table-auto border-collapse overflow-hidden rounded-3xl bg-[#fbfbfb]'
          data-testid='degradation-drivers-table'
        >
          <thead>
            <tr className='border-b border-[#e0e0e0] [&>*]:typography-overline [&>*]:p-6 [&>*]:pb-4 [&>*]:text-text-secondary '>
              <th className='text-start'>
                {t('shared.ncaDetail.habitatIntactness.degradationDriversTable.degradationDrivers')}
              </th>
              {years.map((year) => (
                <th key={year} className='text-end'>
                  {year}
                </th>
              ))}
              <th className='text-end'>
                {t('shared.ncaDetail.habitatIntactness.degradationDriversTable.totalAreaPct')}
              </th>
              <th className='text-end'>
                {t('shared.ncaDetail.habitatIntactness.degradationDriversTable.yearsChange', {
                  years: yearsTimeSpan,
                })}
              </th>
            </tr>
          </thead>
          <tbody className='[&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-[#e0e0e0]'>
            {threatsData.map((threat) => {
              const latestValue = threat.ts[`${currentYear}` as string] as number;
              const baselineValue = threat.ts[`${baselineYear}` as string] as number;

              const latestValueUnit = threat.tsUnits as UnitEnum | null;
              let latestValueInHectares = latestValue;
              if (latestValueUnit === UnitEnum['km^2']) {
                latestValueInHectares = new Area({
                  value: latestValue,
                  dimension: 'squareKilometers',
                }).toHectares().value;
              }

              const changePct = getChangePct({
                baseline: baselineValue,
                latest: latestValue,
              });
              const displayChangePct =
                typeof baselineValue !== 'number' || typeof latestValue !== 'number'
                  ? NO_DATA
                  : `${getDisplayNumber(changePct, undefined, { signDisplay: 'exceptZero' })} ${t(UNIT_LABEL)}`;
              const variant = changePct > 0 ? 'negative' : 'positive';

              const threatAreaValue = getAreaPct({
                area: latestValueInHectares,
                totalArea: projectAreaInHectares,
              });

              const areaFromFact = threat?.areaPct
                ? `${getDisplayNumber(threat?.areaPct)} ${t(UNIT_LABEL)}`
                : undefined;
              const fallbackAreaPercentage =
                threatAreaValue !== null ? `${getDisplayNumber(threatAreaValue)} ${t(UNIT_LABEL)}` : NO_DATA;

              const threatAreaPercentage = areaFromFact ?? fallbackAreaPercentage;

              return (
                <tr
                  key={threat.name}
                  className='text-text-primary [&>*]:typography-body2 hover:bg-neutral-black-4 [&>*]:px-6 [&>*]:py-2'
                >
                  <td>{t(`shared.projects.project.conservation.labels.${threat.name}`)}</td>
                  {years.map((year, index) => {
                    const area = Object.hasOwn(threat.ts, year) ? threat.ts[`${year}`] : undefined;
                    const displayArea =
                      area !== undefined && area !== null
                        ? new Area({
                            value: area,
                            dimension: threat?.tsUnits === UnitEnum['km^2'] ? 'squareKilometers' : 'hectares',
                          }).renderDisplay({ className: 'whitespace-nowrap' })
                        : NO_DATA;

                    return (
                      <td key={`${year}-${index}`} className='text-end'>
                        {displayArea}
                      </td>
                    );
                  })}
                  <td className='text-end'>{threatAreaPercentage}</td>
                  <td className='text-end'>
                    <CapsuleTrend variant={variant}>{displayChangePct}</CapsuleTrend>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Stack>
  );
};

type ThreatData = {
  name: Threat;
  value: number;
  valueUnits: UnitEnum | null;
  ts: Record<string, number>;
  tsUnits: UnitEnum | null;
  areaPct: number | null;
};

const useGetThreatData = (threat: Threat): ThreatData => {
  const timeSeriesData = useFact<Record<string, number>>(`r1_conservation_${threat}_ts` as FactType);
  const latestValue = useFact<number>(`r1_conservation_${threat}` as FactType);
  const areaPct = useFact<number>(`r1_conservation_${threat}_area_pct` as FactType);

  return {
    name: threat,
    value: latestValue?.value,
    valueUnits: latestValue?.unit,
    ts: timeSeriesData?.value,
    tsUnits: timeSeriesData?.unit,
    areaPct: areaPct?.value,
  };
};

const useThreatData = (): ThreatData[] => {
  const threats = [
    useGetThreatData(Threat.built),
    useGetThreatData(Threat.crop),
    useGetThreatData(Threat.road),
    useGetThreatData(Threat.mine),
    useGetThreatData(Threat.treeloss),
    useGetThreatData(Threat.overgrazing),
    useGetThreatData(Threat.other),
    useGetThreatData(Threat.airstripes),
    useGetThreatData(Threat.minor_road),
    useGetThreatData(Threat.major_road),
    useGetThreatData(Threat.fire),
    useGetThreatData(Threat.edge),
    useGetThreatData(Threat.river),
  ];

  return threats
    .filter(({ value }) => value !== null && value !== undefined && value >= 0)
    .sort((a, b) => b.value - a.value) as ThreatData[];
};

export const useHabitatIntactnessYears = () => {
  const maxTimeSpan = 4; // Equals to 5 years, since it considers the current year as well
  const threatsData = useThreatData();
  const years: Record<'min' | 'max' | 'timeSpan', number | null> = {
    min: null,
    max: null,
    timeSpan: maxTimeSpan,
  };
  threatsData.forEach(({ ts }) => {
    const tsYears = Object.entries(ts)
      .filter(([, value]) => !isNullish(value))
      .map(([year]) => Number(year));
    const yearsData = [...tsYears, years.min, years.max].filter((n) => typeof n === 'number') as number[];
    years.min = Math.min(...yearsData);
    years.max = Math.max(...yearsData);
    const timeSpan = typeof years.max === 'number' && typeof years.min === 'number' ? years.max - years.min + 1 : null; // +1 since it includes both the start and end years
    years.timeSpan = Math.min(...([years.timeSpan, timeSpan].filter((n) => typeof n === 'number') as number[]));
  });

  if (years.max !== null && years.min !== null && years.max - years.min > maxTimeSpan) {
    years.min = years.max - maxTimeSpan;
  }

  return years;
};
